import { useRef, useEffect, useState } from "react";
import type { ContextMenuState } from "../types";
import { SlotBackground } from "./SlotBackground";
import { InspectButton } from "./InspectButton";
import { InspectItemModal, type AttachmentSlot } from "./InspectItemModal";
import attachmentTestIcon from "../assets/images/attachment-test-icon.png";

const TEST_ATTACHMENTS: AttachmentSlot[] = [
  { id: "muzzle", label: "Muzzle", iconSrc: attachmentTestIcon },
  { id: "grip", label: "Grip", iconSrc: attachmentTestIcon },
  { id: "scope", label: "Scope" },
];

interface Props {
  state: ContextMenuState;
  onClose: () => void;
  onUse: () => void;
  onDrop: () => void;
  onTake: () => void;
  onSplit: (qty: number) => void;
}

const DEFAULT_STYLE = {
  background: "radial-gradient(ellipse at center, #161616 0%, #0b0b0b 100%)",
  border: "1px solid rgba(255,255,255,0.05)",
  color: "rgba(255,255,255,0.42)",
  transition:
    "color 150ms ease, background 150ms ease, border-color 150ms ease",
} as const;

const HOVER_STYLE = {
  background: "linear-gradient(180deg, #0f2040 0%, #071525 100%)",
  border: "1px solid #137fe3",
  color: "#ffffff",
} as const;

function ActionButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      className="w-full py-[9px] text-[13px] text-center cursor-pointer outline-none rounded-[6px]"
      style={hovered ? HOVER_STYLE : DEFAULT_STYLE}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function StepButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      className="w-9 h-9 flex items-center justify-center rounded-[6px] text-white text-[18px] leading-none cursor-pointer outline-none select-none flex-shrink-0"
      style={{
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.10)",
      }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function ContextMenu({
  state,
  onClose,
  onUse,
  onDrop,
  onTake,
  onSplit,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState({ x: state.x, y: state.y });
  const [splitting, setSplitting] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentSlot[]>(TEST_ATTACHMENTS);
  const [rawInput, setRawInput] = useState("1");
  const [qty, setQty] = useState(1);
  const [inputFocused, setInputFocused] = useState(false);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    setSplitting(false);
    setInspecting(false);
    setQty(1);
    setRawInput("1");
  }, [state.visible, state.item?.id]);

  // Sync text input when qty changes from slider / ± (but not while typing)
  useEffect(() => {
    if (!isFocusedRef.current) setRawInput(String(qty));
  }, [qty]);

  useEffect(() => {
    if (!state.visible || !ref.current) return;
    const { offsetWidth: w, offsetHeight: h } = ref.current;
    setAdjustedPos({
      x: state.x + w > window.innerWidth ? state.x - w : state.x,
      y: state.y + h > window.innerHeight ? state.y - h : state.y,
    });
  }, [state.visible, state.x, state.y, splitting, inspecting]);

  if (!state.visible || !state.item) return null;

  const { item, location } = state;
  const section = location?.section;
  const showDrop = section !== "nearby";
  const showTake = section === "nearby" || section === "carTrunk";
  const showSplit = item.quantity > 1;
  const showInspect = item.type === "weapon";
  const maxQty = item.quantity - 1;
  const clamp = (v: number) => Math.min(maxQty, Math.max(1, v));

  const handleInputChange = (raw: string) => {
    const digitsOnly = raw.replace(/[^0-9]/g, "");
    const n = parseInt(digitsOnly, 10);
    if (!isNaN(n) && n >= 1) {
      const clamped = clamp(n);
      setRawInput(String(clamped));
      setQty(clamped);
    } else {
      setRawInput(digitsOnly);
    }
  };

  const handleInputBlur = () => {
    isFocusedRef.current = false;
    setInputFocused(false);
    const n = parseInt(rawInput, 10);
    const clamped = clamp(isNaN(n) || n < 1 ? 1 : n);
    setRawInput(String(clamped));
    setQty(clamped);
  };

  const confirmSplit = () => {
    onSplit(qty);
    onClose();
  };

  const goBack = () => {
    setSplitting(false);
    setInspecting(false);
    setQty(1);
    setRawInput("1");
  };

  const panelWidth = inspecting ? 260 : 220;

  return (
    <div
      ref={ref}
      className="fixed z-50 overflow-hidden"
      style={{
        top: adjustedPos.y,
        left: adjustedPos.x,
        width: panelWidth,
        background: "#0A0B0B",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 8,
      }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Item preview */}
      <div className="flex gap-2.5 p-2.5">
        <div className="relative flex-shrink-0 w-[68px] h-[68px] 2560px:w-[75px] 2560px:h-[75px]">
          <SlotBackground state="active">
            <div className="relative w-full h-full flex flex-col items-center justify-center">
              <span className="absolute top-1 left-1.5 text-[10px] leading-none text-white/70 select-none pointer-events-none">
                {item.weight}kg
              </span>
              <span className="absolute top-1 right-1.5 text-[10px] leading-none text-white/70 select-none pointer-events-none">
                x{item.quantity}
              </span>
              {item.image && (
                <img
                  src={item.image}
                  alt={item.name}
                  className="object-contain pointer-events-none"
                  style={{ maxWidth: 40, maxHeight: 30 }}
                  draggable={false}
                />
              )}
            </div>
          </SlotBackground>
        </div>
        <div className="flex flex-col justify-center gap-1 min-w-0 flex-1">
          <span className="text-white font-semibold uppercase purista-font leading-tight tracking-wide text-[13px] 2560px:text-[14px]">
            {item.name}
          </span>
          <span className="text-white/55 text-[11px] 2560px:text-[12px] leading-snug line-clamp-2">
            {item.description}
          </span>
        </div>
      </div>

      <div className="h-px bg-white/[0.07]" />

      {inspecting ? (
        <InspectItemModal
          item={item}
          attachments={attachments}
          onRemoveAttachment={(id) =>
            setAttachments((prev) => prev.filter((a) => a.id !== id))
          }
          onBack={goBack}
        />
      ) : splitting ? (
        /* Split quantity selector */
        <div className="flex flex-col gap-2 p-2.5">
          <span className="text-[10px] uppercase tracking-widest text-white/35 text-center">
            Split — how many?
          </span>

          {/* Counter row */}
          <div className="flex items-center gap-1.5">
            <StepButton onClick={() => setQty((q) => clamp(q - 1))}>
              −
            </StepButton>

            <div className="flex-1 flex flex-col items-center gap-0.5">
              <input
                type="text"
                inputMode="numeric"
                value={rawInput}
                className="w-full text-center purista-font font-bold outline-none"
                style={{
                  fontSize: 22,
                  color: "#ffffff",
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${inputFocused ? "#137fe3" : "rgba(255,255,255,0.14)"}`,
                  borderRadius: 6,
                  padding: "7px 6px",
                  boxShadow: inputFocused
                    ? "0 0 0 3px rgba(19,127,227,0.12)"
                    : "none",
                  transition: "border-color 150ms ease, box-shadow 150ms ease",
                }}
                onFocus={(e) => {
                  isFocusedRef.current = true;
                  setInputFocused(true);
                  e.target.select();
                }}
                onBlur={handleInputBlur}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                    return;
                  }
                  const allowed =
                    /^[0-9]$/.test(e.key) ||
                    [
                      "Backspace",
                      "Delete",
                      "ArrowLeft",
                      "ArrowRight",
                      "Tab",
                      "Home",
                      "End",
                    ].includes(e.key);
                  if (!allowed) e.preventDefault();
                }}
              />
              <span className="text-white/30 text-[10px] leading-none">
                / {maxQty} max
              </span>
            </div>

            <StepButton onClick={() => setQty((q) => clamp(q + 1))}>
              +
            </StepButton>
          </div>

          {/* Slider */}
          <input
            type="range"
            min={1}
            max={maxQty}
            value={qty}
            onChange={(e) => setQty(Number(e.target.value))}
            className="w-full cursor-pointer outline-none"
            style={{ accentColor: "#137fe3" }}
          />

          {/* Confirm */}
          <button
            className="w-full py-[9px] text-[13px] text-center cursor-pointer outline-none rounded-[6px] text-white"
            style={{
              background: "linear-gradient(180deg, #0f2040 0%, #071525 100%)",
              border: "1px solid #137fe3",
            }}
            onClick={confirmSplit}
          >
            Split ×{qty}
          </button>

          {/* Back */}
          <button
            className="w-full py-[7px] text-[11px] text-center cursor-pointer outline-none rounded-[6px]"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.07)",
              color: "rgba(255,255,255,0.35)",
            }}
            onClick={goBack}
          >
            Back
          </button>
        </div>
      ) : (
        /* Action buttons */
        <div className="flex flex-col gap-1.5 p-2.5">
          <ActionButton
            label="Use"
            onClick={() => {
              onUse();
              onClose();
            }}
          />
          {showDrop && (
            <ActionButton
              label="Drop"
              onClick={() => {
                onDrop();
                onClose();
              }}
            />
          )}
          {showTake && (
            <ActionButton
              label="Take"
              onClick={() => {
                onTake();
                onClose();
              }}
            />
          )}
          {showSplit && (
            <ActionButton label="Split" onClick={() => setSplitting(true)} />
          )}
          {showInspect && (
            <InspectButton onClick={() => setInspecting(true)} />
          )}
        </div>
      )}
    </div>
  );
}
